import { useCallback, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Control, Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import * as yup from 'yup';

import { departmentsEndpoints } from '@/api/endpoints';
import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import {
  Form as FormProvider,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useAccount } from '@/hooks/useAccount';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useDeskId } from '@/hooks/useDeskId';
import RoutingSelect from '@/modules/departments/components/RoutingSelect/RoutingSelect';
import { useDepartments } from '@/modules/departments/hooks/useDepartments';
import { Form, DepartmentRouting } from '@/modules/departments/models';
import { OptionBase } from '@/shared/types/common';
import { errorMessage } from '@/util/validation/helpers';
import { invitationRules } from '@/util/validation/rules/invitation-rules';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

type FormType = { name: string; users: OptionBase[]; routing: DepartmentRouting; icon?: string };

export const ModalDepartmentCreate = () => {
  const { t } = useTranslation();
  const deskId = useDeskId();
  const { agents } = useAccount();
  const [isLoading, setIsLoading] = useState(false);
  const { createDepartment, addMembersToDepartment } = useDepartments(deskId);
  const closeModal = useCloseModal();

  const departmentSchema = yup.object({
    name: createNameFieldSchema({
      getEndpoint: (name) => departmentsEndpoints.checkName(deskId, name, { desk_id: deskId }),
    }),
    users: yup.array().of(yup.mixed<OptionBase>()).required(),
    routing: yup.string().oneOf(Object.values(DepartmentRouting)).required(),
    icon: yup.string().optional(),
  }) as yup.ObjectSchema<FormType>;

  const formMethods = useForm<FormType>({
    mode: 'onChange',
    resolver: yupResolver(departmentSchema) as Resolver<FormType>,
    defaultValues: { name: '', users: [], routing: DepartmentRouting.BALANCED },
  });
  const {
    handleSubmit,
    formState: { errors, isDirty, isValid },
    control,
    getValues,
  } = formMethods;

  const memberOptions: MultiSelectOption[] = useMemo(
    () =>
      agents?.reduce((acc, user) => {
        acc.push({ value: user.agent_id, label: user.name, description: user.email });
        return acc;
      }, [] as MultiSelectOption[]),
    [agents]
  );

  const onSubmit = useCallback(
    (dpt) => {
      const department = { name: dpt.name, routing: getValues().routing };
      const user_ids = dpt.users.map((m) => m.value);

      createDepartment(department, {
        onSuccess: (resp) => {
          const payload = { department_id: resp.department_id, user_ids };
          addMembersToDepartment(payload, {
            onSuccess: closeModal,
            onError: (err) => {
              toast.error(err.message);
              setIsLoading(false);
            },
          });
        },
        onError: (err) => {
          toast.error(err.message);
          setIsLoading(false);
        },
      });
    },
    [addMembersToDepartment, closeModal, createDepartment, getValues]
  );

  const disabled = !isDirty || !isValid;

  return (
    <FormDialog
      title={t('departments.add_department')}
      submitLabel={t('common.create')}
      onSubmit={handleSubmit(onSubmit)}
      submitDisabled={disabled}
      isLoading={isLoading}
      submitOnEnter
    >
      <FormProvider {...formMethods}>
        <form autoComplete="off" className="space-y-6">
          <FormField
            control={control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.name')}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t('common.name_placeholder')} autoFocus />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <RoutingSelect control={control as unknown as Control<Form>} />
          <div className="input__container">
            <FormField
              control={control}
              name="users"
              rules={invitationRules(t).roles}
              render={({ field }) => {
                const selectedValues = field.value?.map((user: OptionBase) => user.value) || [];

                return (
                  <FormItem>
                    <FormLabel className="text-base font-medium">{t('common.members')}</FormLabel>
                    <FormControl>
                      <MultiSelectField
                        title={t('common.select')}
                        options={memberOptions}
                        value={selectedValues}
                        onChange={(selectedStringValues) => {
                          const selectedUsers = selectedStringValues
                            .map((value) => memberOptions.find((user) => user.value === value))
                            .filter((user): user is OptionBase => !!user);
                          field.onChange(selectedUsers);
                        }}
                        placeholder={t('departments.agents_placeholder')}
                        disabled={!agents}
                        error={!!errors?.users?.message}
                      />
                    </FormControl>
                    {errors?.users?.message && (
                      <FormMessage>{errorMessage({ field: errors.users })}</FormMessage>
                    )}
                  </FormItem>
                );
              }}
            />
          </div>
        </form>
      </FormProvider>
    </FormDialog>
  );
};
